import _ from 'lodash';
import Joi from 'joi';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import {
  useCallback, useMemo, useState, useReducer, useEffect, useRef,
} from 'react';

const useIsMount = () => {
  const [isMount, setIsMount] = useState(true);
  useEffect(() => {
    setIsMount(false);
  }, []);
  return isMount;
};
const useEffectSkipMount = (fn, deps) => {
  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      return;
    }
    fn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const useIsMounted = () => {
  const isMountedRef = useRef(true);
  useEffect(() => () => (isMountedRef.current = false), []);
  return isMountedRef;
};
const defaultClone = _.cloneDeep;

const errorsReducer = (state, { type, field, value }) => {
  switch (type) {
    case 'SET_ALL': return value;
    case 'SET_VALUE': return { ...state, [field]: value };
    default: throw new Error('Invalid action');
  }
};
const sourceReducer = (state, { type, field, value }) => {
  switch (type) {
    case 'SET_ALL': return value;
    case 'SET_VALUE': return ((state[field] = value), state);
    default: throw new Error('Invalid action');
  }
};

export const useFieldsetLite = ({
  schema,
  onSubmit,
  cloneSource = defaultClone,
  source: templateSource,
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useReducer(errorsReducer, {});
  const [source, setSource] = useReducer(sourceReducer, undefined, () => cloneSource(templateSource || {}));
  const isMounted = useIsMounted();

  useEffectSkipMount(() => {
    setLoading(false);
    setErrors({ type: 'SET_ALL', value: {} });
    setSource({ type: 'SET_ALL', value: cloneSource(templateSource || {}) });
  }, [cloneSource, schema, templateSource]);

  const submit = useCallback(async (e) => {
    _.invoke(onSubmit && e, 'preventDefault');
    setLoading(true);
    setErrors({ type: 'SET_ALL', value: {} });

    const { error, value: validatedSource } = Joi.object(schema).validate(
      source,
      { abortEarly: false, allowUnknown: true, errors: { wrap: { label: false } } },
    );

    if (error) {
      _.invoke(!onSubmit && e, 'preventDefault'); // cancel form submit in case of traditional form validation
      console.warn('validation failed', error.details);
      setLoading(false);
      setErrors({ type: 'SET_ALL', value: _.transform(error.details, (err, { path: [key], message }) => (err[key] = message), {}) });
      return false;
    } if (!onSubmit) { // no need to do more work in case of a traditional form validation
      return true;
    }

    const newSource = _.pickBy(validatedSource, _.negate(_.isUndefined));
    const success = await onSubmit(newSource);

    if (isMounted.current) {
      setLoading(false);
      setErrors({ type: 'SET_ALL', value: {} });
    }

    return success;
  }, [onSubmit, schema, source, isMounted]);

  const fields = _.mapValues(
    useMemo(() => _.mapValues(schema, (ignored, field) => createSelector(
      _.iteratee('value'), _.iteratee('error'),
      (currentValue, error) => ({
        value: currentValue,
        message: error,
        errored: !!error,
        onChange: (value, { value: maybeValue, checked: maybeValue2 } = {}) => {
          const newVal = _.has(value, 'target.value') ? value.target.value : _.has(value, 'target.checked') ? value.target.checked : !_.isUndefined(maybeValue) ? maybeValue : (!_.isUndefined(maybeValue2) ? maybeValue2 : value);
          setSource({ type: 'SET_VALUE', field, value: newVal });
          setErrors({ type: 'SET_VALUE', field, value: undefined });
          return true;
        },
      }),
    )), [schema]),
    (creator, field) => creator({ value: source[field], error: errors[field] }),
  );

  return { fields, loading, submit };
};

useFieldsetLite.fieldProp = PropTypes.exact({
  onChange: PropTypes.func.isRequired,
  message: PropTypes.string,
  errored: PropTypes.bool.isRequired,
  value: PropTypes.any,
});
